// ComposeDialog — new-thread composer (also used for forward with pre-filled values).

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Minus, Paperclip, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAccounts } from '@/lib/accounts';
import { useComposeThread } from '@/lib/collab';
import {
  useAttachmentUploads,
  useFileDrop,
  formatBytes,
  type PendingUpload,
} from '@/lib/attachments';
import { signaturesForAccount, useSignatures } from '@/lib/signatures';
import {
  fetchComposeDraft,
  useAddDraftComment,
  useComposeDraftComments,
  useDeleteDraftComment,
  useSaveComposeDraft,
} from '@/lib/compose-drafts';
import { useMe } from '@/lib/auth';
import { SignatureInlinePreview } from '@/components/SignatureInlinePreview';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/RichTextEditor';
import { RecipientChips } from '@/components/RecipientChips';
import { TemplatePicker } from '@/components/TemplatePicker';
import { SendSplitButton, formatSendAt } from '@/components/SendSplitButton';
import { confirmPreSend, preSendWarnings } from '@/lib/presend';
import { MenuPicker } from '@/components/ui/menu-picker';

export interface ForwardAttachmentRef {
  /** Authenticated proxy URL for the original attachment's bytes. */
  url: string;
  filename: string;
  mimeType: string;
}

export interface ComposeInitialValues {
  /** Pre-select a specific account (e.g. the account a forwarded email came from). */
  accountId?: number;
  /** Pre-fill the To field (comma-separated string). */
  to?: string;
  /** Pre-fill the Cc field. */
  cc?: string;
  /** Pre-fill the Subject field. */
  subject?: string;
  /** Pre-fill the Body textarea (used for forward attribution). */
  body?: string;
  /** Original attachments to carry along on a forward — fetched via the
   *  download proxy and re-uploaded as outgoing attachments on open. */
  forwardAttachments?: ForwardAttachmentRef[];
}

const INLINE_IMAGE_RE = /^image\/(png|jpe?g|webp|gif)$/i;

interface ComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the dialog pre-fills all fields and shows "Forward" title. */
  initial?: ComposeInitialValues;
  /** Desktop dock position — distance from the right edge in rem. Lets several
   *  compose windows sit side-by-side (and to the left of the reply dock). */
  slotRightRem?: number;
  /** Per-window localStorage key so simultaneous composers don't clobber each
   *  other's crash-recovery drafts. */
  storageKey?: string;
  /** Open an existing SERVER compose draft (from the Drafts view) — loads its
   *  content, enables the comment stream, and Send deletes it. */
  draftId?: number;
  /** Restored after a reload: open collapsed to the title bar. */
  initialMinimized?: boolean;
  /** The window acquired (autosave) or dropped (discard) a server draft id —
   *  the Inbox root persists it so the window survives a reload. */
  onDraftIdChange?: (id: number | null) => void;
}

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// --- Compose autosave -------------------------------------------------------
// A new message has no thread/server-draft to fall back to, so we mirror the
// in-progress message to localStorage on every change. A refresh/crash then
// restores it on next open. Cleared on send or discard.
export const COMPOSE_DRAFT_KEY = 'wharf:compose-draft:v1';

interface ComposeDraftSnapshot {
  accountId: number | '';
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  /** Completed uploads' chips — the files live server-side, so a crash
   *  recovery can restore them instead of dropping the attachments. */
  atts?: { id: number; filename: string; mimeType: string; sizeBytes: number }[];
  savedAt?: number;
}

function loadComposeDraft(key: string): ComposeDraftSnapshot | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const d = JSON.parse(raw) as Record<string, unknown>;
    return {
      accountId: typeof d.accountId === 'number' ? d.accountId : '',
      to: Array.isArray(d.to) ? (d.to as string[]) : [],
      cc: Array.isArray(d.cc) ? (d.cc as string[]) : [],
      bcc: Array.isArray(d.bcc) ? (d.bcc as string[]) : [],
      subject: typeof d.subject === 'string' ? d.subject : '',
      bodyHtml: typeof d.bodyHtml === 'string' ? d.bodyHtml : '',
      atts: Array.isArray(d.atts) ? (d.atts as ComposeDraftSnapshot['atts']) : [],
    };
  } catch {
    return null;
  }
}

function saveComposeDraft(key: string, d: ComposeDraftSnapshot): void {
  try {
    localStorage.setItem(key, JSON.stringify({ ...d, savedAt: Date.now() }));
  } catch {
    /* quota / private mode — best-effort */
  }
}

function clearComposeDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Drop crash-recovery drafts from secondary compose windows after a week —
 *  they have no UI to resurface them and shouldn't pile up forever. */
export function pruneStaleComposeDrafts(): void {
  try {
    const cutoff = Date.now() - 7 * 24 * 3600_000;
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${COMPOSE_DRAFT_KEY}:`)) continue;
      try {
        const d = JSON.parse(localStorage.getItem(key) ?? '{}') as { savedAt?: number };
        if (!d.savedAt || d.savedAt < cutoff) localStorage.removeItem(key);
      } catch {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

export function ComposeDialog({
  open,
  onOpenChange,
  initial,
  slotRightRem = 1.5,
  storageKey = COMPOSE_DRAFT_KEY,
  draftId,
  initialMinimized = false,
  onDraftIdChange,
}: ComposeDialogProps) {
  const { data: accountsData } = useAccounts();
  const me = useMe();
  const saveDraftMut = useSaveComposeDraft();
  // Set when this window is editing/viewing a server draft (either opened with
  // one, or after the user hits Save draft).
  const [serverDraftId, setServerDraftId] = useState<number | null>(draftId ?? null);
  const [draftAuthorId, setDraftAuthorId] = useState<number | null>(null);
  const accounts = accountsData?.accounts ?? [];
  const compose = useComposeThread();
  const sigs = useSignatures();
  const qc = useQueryClient();

  const [accountId, setAccountId] = useState<number | ''>('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sigId, setSigId] = useState<number | null>(null);
  const [minimized, setMinimized] = useState(false);
  const rteRef = useRef<RichTextEditorHandle | null>(null);
  const att = useAttachmentUploads();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Drag-and-drop with a self-expiring veil (can't get stuck — see useFileDrop).
  const { dragging, rootProps: dropProps } = useFileDrop((files) => {
    const images = files.filter((f) => INLINE_IMAGE_RE.test(f.type));
    const rest = files.filter((f) => !INLINE_IMAGE_RE.test(f.type));
    // Images go inline into the body (the send path converts them to proper
    // cid: parts); everything else becomes a regular attachment.
    if (images.length > 0) rteRef.current?.insertImageFiles(images);
    if (rest.length > 0) att.addFiles(rest);
  });
  // Snapshot of the just-sent message, kept through the 30s undo window so
  // 'Undo' can re-open the composer with everything intact (Gmail-style) — a
  // new-message send has no thread/draft to fall back to otherwise.
  const restoreRef = useRef<{
    accountId: number | '';
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    bodyHtml: string;
    uploads: PendingUpload[];
  } | null>(null);
  const restoringRef = useRef(false);

  // Hydrate / reset whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    // Re-opening because the user hit Undo: restore the snapshot verbatim
    // (recipients, subject, rich body) rather than resetting to a blank form.
    if (restoringRef.current && restoreRef.current) {
      restoringRef.current = false;
      const p = restoreRef.current;
      setAccountId(p.accountId);
      setTo(p.to);
      setCc(p.cc);
      setBcc(p.bcc);
      setSubject(p.subject);
      setShowCc(p.cc.length > 0);
      setShowBcc(p.bcc.length > 0);
      setSigId(null);
      setMinimized(false);
      compose.reset();
      att.restore(p.uploads);
      setTimeout(() => rteRef.current?.setHtml(p.bodyHtml), 0);
      return;
    }
    // Opening a saved server draft (from the Drafts view): load its content.
    if (draftId) {
      att.clear();
      setMinimized(initialMinimized);
      setSigId(null);
      compose.reset();
      void fetchComposeDraft(draftId)
        .then((d) => {
          setServerDraftId(d.id);
          setDraftAuthorId(d.authorUserId);
          setAccountId(d.accountId ?? '');
          setTo(d.to);
          setCc(d.cc);
          setBcc(d.bcc);
          setSubject(d.subject);
          setShowCc(d.cc.length > 0);
          setShowBcc(d.bcc.length > 0);
          if (d.attachments?.length) {
            att.restore(
              d.attachments.map((a) => ({
                localId: `srv-${a.id}`,
                filename: a.filename,
                sizeBytes: a.sizeBytes,
                status: 'done' as const,
                uploaded: a,
              })),
            );
          }
          setTimeout(() => rteRef.current?.setHtml(d.bodyHtml), 0);
        })
        .catch(() => toast.error('Could not load the draft'));
      return;
    }
    // A forward/prefilled open (initial) wins; otherwise restore an autosaved
    // draft if one survived a refresh/crash.
    att.clear();
    const saved = initial ? null : loadComposeDraft(storageKey);
    // Forwarding: pull each original attachment through the download proxy and
    // re-upload it as an outgoing attachment, so forwards keep their files.
    for (const fa of initial?.forwardAttachments ?? []) {
      void fetch(fa.url, { credentials: 'include' })
        .then(async (r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const blob = await r.blob();
          att.addFiles([new File([blob], fa.filename, { type: fa.mimeType })]);
        })
        .catch(() => toast.error(`Couldn't carry over "${fa.filename}" — attach it manually`));
    }
    // Crash-recovery: bring the attachment chips back too — the uploads are
    // still on the server, only the chip list lived in this component.
    if (saved?.atts?.length) {
      att.restore(
        saved.atts.map((a) => ({
          localId: `ls-${a.id}`,
          filename: a.filename,
          sizeBytes: a.sizeBytes,
          status: 'done' as const,
          uploaded: a,
        })),
      );
    }
    const defaultAccount = saved?.accountId ?? initial?.accountId ?? accounts[0]?.id ?? '';
    setAccountId(defaultAccount);
    setTo(saved?.to ?? (initial?.to ? parseEmails(initial.to) : []));
    setCc(saved?.cc ?? (initial?.cc ? parseEmails(initial.cc) : []));
    setBcc(saved?.bcc ?? []);
    setSubject(saved?.subject ?? initial?.subject ?? '');
    setShowCc((saved ? saved.cc.length > 0 : !!initial?.cc));
    setShowBcc((saved?.bcc.length ?? 0) > 0);
    setSigId(null);
    setMinimized(false);
    compose.reset();
    // Saved draft body is already rich HTML; a forward body arrives as plain
    // text, so escape + convert newlines for the contentEditable.
    const initialHtml = saved
      ? saved.bodyHtml
      : initial?.body
        ? initial.body
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\r?\n/g, '<br>')
        : '';
    // Defer until the dialog content is mounted.
    setTimeout(() => rteRef.current?.setHtml(initialHtml), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasContent =
    bodyText.trim().length > 0 ||
    to.length > 0 ||
    cc.length > 0 ||
    bcc.length > 0 ||
    subject.trim().length > 0;
  // Crash-recovery mirror to localStorage on every change (same-device only;
  // the server autosave below is the real safety net).
  useEffect(() => {
    if (!open) return;
    const atts = att.uploads
      .filter((u) => u.status === 'done' && u.uploaded)
      .map((u) => u.uploaded!);
    if (hasContent || atts.length > 0) {
      saveComposeDraft(storageKey, { accountId, to, cc, bcc, subject, bodyHtml, atts });
    } else clearComposeDraft(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId, to, cc, bcc, subject, bodyHtml, bodyText, att.uploads]);

  // Pick the first account once accounts load (in case they arrived after open).
  useEffect(() => {
    const first = accounts[0];
    if (accountId === '' && first) {
      setAccountId(first.id);
    }
  }, [accounts, accountId]);

  const dirty = bodyText.trim().length > 0 || to.length > 0 || subject.trim().length > 0;

  function submit(opts: { scheduledAt?: string } = {}) {
    if (!accountId || to.length === 0 || !bodyText.trim()) return;
    if (att.anyUploading) {
      toast.info('Hold on — attachment still uploading');
      return;
    }
    if (
      !confirmPreSend(
        preSendWarnings({
          bodyText,
          recipients: [...to, ...cc],
          attachmentCount: att.readyIds.length,
          subject,
          checkSubject: true,
        }),
      )
    ) {
      return;
    }
    // Snapshot now so Undo can bring the whole message back — attachments
    // included (the cancel un-consumes their server rows).
    restoreRef.current = {
      accountId,
      to: [...to],
      cc: [...cc],
      bcc: [...bcc],
      subject,
      bodyHtml,
      uploads: [...att.uploads],
    };
    compose.mutate(
      {
        accountId: Number(accountId),
        to,
        cc: cc.length ? cc : undefined,
        bcc: bcc.length ? bcc : undefined,
        subject: subject.trim() || undefined,
        body: bodyText.trim(),
        bodyHtml,
        signatureId: sigId,
        attachmentIds: att.readyIds.length > 0 ? att.readyIds : undefined,
        scheduledAt: opts.scheduledAt,
      },
      {
        onSuccess: (resp) => {
          onOpenChange(false);
          clearComposeDraft(storageKey);
          att.clear();
          // The message left the building — its server draft is done.
          if (serverDraftId) {
            void fetch(`/api/compose-drafts/${serverDraftId}`, {
              method: 'DELETE',
              credentials: 'include',
            }).finally(() => void qc.invalidateQueries({ queryKey: ['compose-drafts'] }));
          }
          // Surface the 'Sending…' row in the Sent folder immediately.
          void qc.invalidateQueries({ queryKey: ['outgoing'] });
          const isScheduled =
            opts.scheduledAt && new Date(opts.scheduledAt).getTime() > Date.now() + 35_000;
          toast.success(
            isScheduled ? `Scheduled for ${formatSendAt(opts.scheduledAt!)}` : 'Sending…',
            {
            duration: isScheduled ? 8_000 : 30_000,
            description: isScheduled ? undefined : 'Leaves in 30 seconds.',
            action: {
              label: isScheduled ? 'Cancel' : 'Undo',
              onClick: () => {
                void fetch(`/api/outgoing/${resp.outgoingId}/cancel`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ restoreDraft: false }),
                }).then((r) => {
                  if (r.ok) {
                    void qc.invalidateQueries({ queryKey: ['outgoing'] });
                    // Re-open the composer with the message restored. The
                    // server draft was already DELETEd on send, so detach its
                    // id — otherwise Save draft PUTs a dead row (404 with a
                    // misleading "only the author can edit" toast) and the
                    // comments strip polls a deleted draft forever.
                    setServerDraftId(null);
                    restoringRef.current = true;
                    onOpenChange(true);
                    toast.info('Send cancelled — your message is back');
                  } else {
                    toast.error('Too late to undo — the email already went out');
                  }
                });
              },
            },
          });
        },
      },
    );
  }

  const canSend = !!accountId && to.length > 0 && bodyText.trim().length > 0;
  const accountSigs = signaturesForAccount(
    sigs.data?.signatures,
    typeof accountId === 'number' ? accountId : null,
  );
  const hasSig = accountSigs.length > 0;
  const isForward = !!initial?.body;

  /** Close = KEEP (Gmail semantics): the message stays in the shared Drafts
   *  list. Flushes the latest autosave first; falls back to the on-device
   *  copy if the server can't be reached. */
  function requestClose() {
    if (dirty && !isForeignDraft) {
      void saveToServer().then((ok) => {
        if (ok) {
          clearComposeDraft(storageKey);
          toast.success('Kept in Drafts');
        } else {
          toast.info("Couldn't reach the server — kept on this device for now");
        }
      });
    } else {
      clearComposeDraft(storageKey);
      att.clear();
    }
    onOpenChange(false);
  }

  /** Discard = the only path that deletes: server draft, uploads, local copy. */
  function discardMessage() {
    if (dirty && !confirm('Discard this message? It will be removed from Drafts too.')) return;
    if (serverDraftId != null && !isForeignDraft) {
      void fetch(`/api/compose-drafts/${serverDraftId}`, {
        method: 'DELETE',
        credentials: 'include',
      }).finally(() => void qc.invalidateQueries({ queryKey: ['compose-drafts'] }));
    }
    clearComposeDraft(storageKey);
    att.discardAll();
    onDraftIdChange?.(null);
    onOpenChange(false);
  }

  /** Save (or update) the server draft: shared with the team under Drafts,
   *  commentable. Saves and closes, Gmail-style. */
  function handleSaveDraft() {
    saveDraftMut.mutate(
      {
        id: serverDraftId ?? undefined,
        accountId: typeof accountId === 'number' ? accountId : null,
        to,
        cc,
        bcc,
        subject,
        bodyHtml,
        attachmentIds: att.readyIds,
      },
      {
        onSuccess: () => {
          clearComposeDraft(storageKey);
          toast.success(
            serverDraftId
              ? 'Draft updated'
              : 'Draft saved — the team can open and comment on it under Drafts',
          );
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error(
            e instanceof Error && e.message === 'not_found_or_not_author'
              ? "Only the draft's author can edit it — add a comment instead"
              : 'Could not save the draft',
          ),
      },
    );
  }

  const isForeignDraft =
    serverDraftId != null && draftAuthorId != null && me.data?.user.id !== draftAuthorId;

  // --- Server autosave -------------------------------------------------------
  // A new message is a draft from the first keystroke, exactly like a reply:
  // debounced save to the shared Drafts list (create on first save, update
  // after), so a reload, another device, or a closed window can't lose it.
  // Saves are serialised through a promise chain so Close can await the
  // latest one. Never for someone else's draft, never while sending.
  const [autoSave, setAutoSave] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const latestRef = useRef({ accountId, to, cc, bcc, subject, bodyHtml, attIds: att.readyIds, serverDraftId, hasContent });
  latestRef.current = { accountId, to, cc, bcc, subject, bodyHtml, attIds: att.readyIds, serverDraftId, hasContent };
  const saveChain = useRef<Promise<boolean>>(Promise.resolve(true));
  function saveToServer(): Promise<boolean> {
    const run = saveChain.current.then(async () => {
      const v = latestRef.current;
      if (!v.hasContent) return true;
      setAutoSave('saving');
      try {
        const res = await saveDraftMut.mutateAsync({
          id: v.serverDraftId ?? undefined,
          accountId: typeof v.accountId === 'number' ? v.accountId : null,
          to: v.to,
          cc: v.cc,
          bcc: v.bcc,
          subject: v.subject,
          bodyHtml: v.bodyHtml,
          attachmentIds: v.attIds,
        });
        if (v.serverDraftId == null) {
          setServerDraftId(res.id);
          setDraftAuthorId(me.data?.user.id ?? null);
          latestRef.current.serverDraftId = res.id;
          onDraftIdChange?.(res.id);
        }
        setAutoSave('saved');
        return true;
      } catch {
        setAutoSave('error');
        return false;
      }
    });
    saveChain.current = run.catch(() => false);
    return run;
  }
  useEffect(() => {
    if (!open || isForeignDraft || compose.isPending || !hasContent) return;
    const t = setTimeout(() => void saveToServer(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId, to, cc, bcc, subject, bodyHtml, att.readyIds.join(','), hasContent, isForeignDraft]);

  if (!open) return null;

  // Docked Gmail-style composer: floats bottom-right with NO backdrop, so the
  // rest of the inbox stays fully interactive while writing. Minimize
  // collapses to just the title bar. Portal to <body> so no panel/transform
  // ancestor can clip the fixed positioning.
  return createPortal(
    <div
      role="dialog"
      aria-label={isForward ? 'Forward message' : 'New message'}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          if (!dirty) onOpenChange(false);
          else setMinimized(true);
        }
      }}
      {...dropProps}
      style={{ '--dock-right': `${slotRightRem}rem` } as React.CSSProperties}
      className={cn(
        'fixed z-50 flex flex-col border bg-card shadow-2xl',
        // Mobile: full-width bottom sheet. Desktop: docked window whose right
        // offset comes from its slot, so several composers sit side-by-side.
        'inset-x-0 bottom-0 rounded-t-xl md:inset-x-auto md:bottom-0 md:right-[var(--dock-right)] md:w-[34rem] md:rounded-t-lg md:border-b-0',
        minimized ? 'h-auto' : 'max-h-[92dvh] md:max-h-[80vh]',
      )}
    >
      {/* Drop veil */}
      {dragging && !minimized && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-t-xl border-2 border-dashed border-primary/60 bg-primary/5 md:rounded-t-lg">
          <div className="flex items-center gap-2 rounded-md bg-card px-3 py-2 text-sm shadow">
            <Upload className="size-4" strokeWidth={1.75} />
            Drop images inline · files attach
          </div>
        </div>
      )}
      {/* Title bar */}
      <div
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 rounded-t-xl border-b bg-secondary/70 px-4 py-2 md:rounded-t-lg',
          minimized && 'cursor-pointer border-b-0',
        )}
        onClick={() => {
          if (minimized) setMinimized(false);
        }}
      >
        <span className="min-w-0 truncate text-sm font-semibold">
          {isForward ? 'Forward message' : subject.trim() || 'New message'}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={(e) => {
              e.stopPropagation();
              setMinimized((m) => !m);
            }}
            aria-label={minimized ? 'Expand composer' : 'Minimize composer'}
          >
            <Minus className="size-3.5" strokeWidth={2.5} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={(e) => {
              e.stopPropagation();
              requestClose();
            }}
            aria-label="Close composer"
            title="Close (kept in Drafts)"
          >
            <X className="size-3.5" strokeWidth={2.5} />
          </Button>
        </div>
      </div>

      {/* Body stays mounted when minimized (just hidden) so the editor's
          content is never destroyed — unmounting a contentEditable wipes it. */}
        <div className={cn('flex min-h-0 flex-col overflow-y-auto', minimized && 'hidden')}>
          {/* From — account picker */}
          <div className="flex items-center gap-3 border-b px-6 py-2">
            <span className="w-12 shrink-0 text-right text-xs text-muted-foreground">From</span>
            <MenuPicker
              variant="inline"
              className="flex-1 justify-between"
              value={accountId === '' ? undefined : String(accountId)}
              onChange={(v) => setAccountId(Number(v))}
              placeholder="Select account…"
              options={accounts.map((a) => ({
                value: String(a.id),
                label: a.nickname ? `${a.nickname} <${a.emailAddress}>` : a.emailAddress,
              }))}
            />
          </div>

          {/* To */}
          <div className="flex items-start gap-3 border-b px-6 py-2.5">
            <span className="w-12 shrink-0 pt-1 text-right text-xs text-muted-foreground">To</span>
            <RecipientChips
              value={to}
              onChange={setTo}
              placeholder="recipient@example.com"
              autoFocus={!isForward}
            />
            <div className="flex shrink-0 gap-2 pt-1 text-[10px] text-muted-foreground">
              {!showCc && (
                <button
                  type="button"
                  onClick={() => setShowCc(true)}
                  className="hover:text-foreground"
                >
                  Cc
                </button>
              )}
              {!showBcc && (
                <button
                  type="button"
                  onClick={() => setShowBcc(true)}
                  className="hover:text-foreground"
                >
                  Bcc
                </button>
              )}
            </div>
          </div>

          {/* Cc */}
          {showCc && (
            <div className="flex items-start gap-3 border-b px-6 py-2.5">
              <span className="w-12 shrink-0 pt-1 text-right text-xs text-muted-foreground">Cc</span>
              <RecipientChips value={cc} onChange={setCc} placeholder="cc@example.com" />
            </div>
          )}

          {/* Bcc */}
          {showBcc && (
            <div className="flex items-start gap-3 border-b px-6 py-2.5">
              <span className="w-12 shrink-0 pt-1 text-right text-xs text-muted-foreground">Bcc</span>
              <RecipientChips value={bcc} onChange={setBcc} placeholder="bcc@example.com" />
            </div>
          )}

          {/* Subject */}
          <div className="flex items-center gap-3 border-b px-6 py-2.5">
            <span className="w-12 shrink-0 text-right text-xs text-muted-foreground">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="(no subject)"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
            />
          </div>

          {/* Body — rich text */}
          <div className="px-4 py-3">
            <RichTextEditor
              ref={rteRef}
              onChange={(v) => {
                setBodyHtml(v.html);
                setBodyText(v.text);
              }}
              placeholder="Write your message…"
              disabled={compose.isPending}
              minHeightClass={isForward ? 'min-h-[40dvh]' : 'min-h-[32dvh]'}
              onMetaEnter={submit}
            />
          </div>
          {/* Attachment chips */}
          {att.uploads.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-6 pb-2">
              {att.uploads.map((u) => (
                <span
                  key={u.localId}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]',
                    u.status === 'error'
                      ? 'border-destructive/40 bg-destructive/10 text-destructive'
                      : 'bg-card',
                  )}
                >
                  {u.status === 'uploading' ? (
                    <Loader2 className="size-3 animate-spin" strokeWidth={2} />
                  ) : (
                    <Paperclip className="size-3" strokeWidth={2} />
                  )}
                  <span className="max-w-[160px] truncate">{u.filename}</span>
                  <span className="text-muted-foreground">{formatBytes(u.sizeBytes)}</span>
                  {u.error && <span>· {u.error}</span>}
                  <button
                    type="button"
                    onClick={() => att.remove(u.localId)}
                    className="rounded p-0.5 opacity-60 hover:opacity-100"
                    aria-label="Remove attachment"
                  >
                    <X className="size-2.5" strokeWidth={2.5} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {hasSig && typeof accountId === 'number' && (
            <div className="px-6 pb-3">
              {accountSigs.length > 1 && (
                <div className="mb-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span>Signature:</span>
                  <MenuPicker
                    variant="inline"
                    value={String(sigId ?? accountSigs[0]!.id)}
                    onChange={(v) => setSigId(Number(v))}
                    options={accountSigs.map((sg) => ({
                      value: String(sg.id),
                      label: sg.name + (sg.isDefault ? ' (default)' : ''),
                    }))}
                  />
                </div>
              )}
              <SignatureInlinePreview accountId={accountId} signatureId={sigId} />
            </div>
          )}
        </div>

        {/* Team comments on the saved draft (never sent with the email) */}
        {serverDraftId != null && !minimized && (
          <DraftCommentsStrip
            draftId={serverDraftId}
            meId={me.data?.user.id ?? null}
            foreign={isForeignDraft}
          />
        )}

        {/* Footer */}
        <div
          className={cn(
            'flex shrink-0 items-center justify-between border-t px-4 py-3 md:px-6',
            minimized && 'hidden',
          )}
        >
          <div className="flex items-center gap-1.5">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) att.addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach files"
              title="Attach files (max 20 MB each) — drop or paste images to embed them"
            >
              <Paperclip className="size-3.5" strokeWidth={1.75} />
            </Button>
            <TemplatePicker onInsert={(html) => rteRef.current?.insertHtml(html)} />
            {compose.error ? (
              <p className="text-xs text-destructive">
                {compose.error instanceof Error ? compose.error.message : 'Send failed'}
              </p>
            ) : (
              <span className="text-xs text-muted-foreground">
                ⌘↵ to send
              </span>
            )}
            {!isForeignDraft && autoSave !== 'idle' && (
              <span className="text-[10px] text-muted-foreground">
                {autoSave === 'saving' ? 'Saving…' : autoSave === 'saved' ? 'Saved to Drafts' : 'Not saved'}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {(dirty || serverDraftId != null) && !isForeignDraft && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSaveDraft}
                disabled={compose.isPending || saveDraftMut.isPending}
                title="Save to the shared Drafts list — teammates can open and comment"
              >
                {saveDraftMut.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Save draft
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={discardMessage}
              disabled={compose.isPending}
              title="Delete this message (X just closes it — it stays in Drafts)"
            >
              Discard
            </Button>
            <SendSplitButton
              disabled={!canSend || compose.isPending}
              loading={compose.isPending}
              primaryLabel="Send"
              onSendNow={() => submit()}
              onSchedule={(d) => submit({ scheduledAt: d.toISOString() })}
            />
          </div>
        </div>
    </div>,
    document.body,
  );
}

/** Compact comment stream on a saved compose draft — the collaboration
 *  surface. Comments are team-internal and never sent with the email. */
function DraftCommentsStrip({
  draftId,
  meId,
  foreign,
}: {
  draftId: number;
  meId: number | null;
  foreign: boolean;
}) {
  const q = useComposeDraftComments(draftId);
  const add = useAddDraftComment(draftId);
  const del = useDeleteDraftComment(draftId);
  const [text, setText] = useState('');
  const comments = q.data?.comments ?? [];

  function post() {
    const t = text.trim();
    if (!t) return;
    add.mutate(t, { onSuccess: () => setText('') });
  }

  return (
    <div className="shrink-0 border-t bg-muted/20">
      {foreign && (
        <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-1 text-[11px] text-amber-700 dark:text-amber-300">
          A teammate's draft — you can comment (and send it); only the author can edit the saved copy.
        </div>
      )}
      <div className="max-h-32 space-y-1.5 overflow-y-auto px-4 py-2">
        {comments.length === 0 && (
          <p className="text-[11px] text-muted-foreground">
            Team comments — visible to everyone with access, never sent with the email.
          </p>
        )}
        {comments.map((c) => (
          <div key={c.id} className="group flex items-start gap-1.5 text-xs">
            <span className="shrink-0 font-medium">{c.author}:</span>
            <span className="min-w-0 flex-1 whitespace-pre-wrap">{c.body}</span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
            {c.authorUserId === meId && (
              <button
                type="button"
                onClick={() => del.mutate(c.id)}
                className="opacity-0 hover:!opacity-100 group-hover:opacity-60"
                aria-label="Delete comment"
              >
                <X className="size-3" strokeWidth={2} />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 px-4 pb-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') post();
          }}
          placeholder="Comment for the team…"
          className="h-7 flex-1 rounded-md border bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-ring"
        />
        <Button size="xs" variant="secondary" disabled={!text.trim() || add.isPending} onClick={post}>
          Post
        </Button>
      </div>
    </div>
  );
}
